import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import "./StatLeaders.css";

const LABEL = { pts: "Points", ast: "Assists", fg3m: "3-Pointers Made", reb: "Rebounds", blk: "Blocks", stl: "Steals" };
const ABBR = { pts: "PTS", ast: "AST", fg3m: "3PM", reb: "REB", blk: "BLK", stl: "STL" };
const teamLogo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);

function LeaderCard({ stat, rows, scope, onComplete }) {
  return (
    <section className="sl-card" data-testid={`sl-card-${stat}`}>
      <header className="sl-card-head">
        <span className="sl-card-name">{LABEL[stat]}</span>
        <span className="sl-card-abbr">{ABBR[stat]}</span>
      </header>
      {rows.length === 0 ? (
        <p className="sl-empty">No data yet</p>
      ) : rows.map((r, i) => (
        <div className="sl-row" key={(scope === "team" ? r.team_id : r.player_id) || i} data-testid={`sl-row-${stat}-${i}`}>
          <span className="sl-rank">{i + 1}</span>
          {scope === "team" ? (
            <img className="sl-logo" src={teamLogo(r.team)} alt="" loading="lazy" />
          ) : (
            <span className="sl-avatar">{r.headshot ? <img src={r.headshot} alt="" loading="lazy" /> : <b>{(r.name || "?")[0]}</b>}</span>
          )}
          <span className="sl-ident">
            <b>{scope === "team" ? (r.team_name || r.team) : r.name}</b>
            <small>{scope === "team" ? `${r.gp} GP` : r.team}</small>
          </span>
          <strong className="sl-val">{r.value}</strong>
        </div>
      ))}
      {scope === "player" && (
        <button type="button" className="sl-complete" onClick={() => onComplete(stat)} data-testid={`sl-complete-${stat}`}>Complete Leaders</button>
      )}
    </section>
  );
}

export default function StatLeaders() {
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("player");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/stat-leaders?limit=5")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(errorText(e)));
    return () => { alive = false; };
  }, []);

  if (error) return <div className="sl-wrap"><div className="sl-state">{error}</div></div>;
  if (!data) return <div className="sl-wrap"><div className="sl-state">Loading leaders…</div></div>;

  const groups = [["Offensive Leaders", data.offense], ["Defensive Leaders", data.defense]];
  return (
    <div className="sl-wrap" data-testid="stat-leaders-page">
      <div className="sl-top">
        <div>
          <span className="sl-overline">League Leaders</span>
          <h1 className="sl-title">NBA Stat Leaders <em>{data.season_label}</em></h1>
        </div>
        <div className="sl-toggle" role="tablist" aria-label="Scope">
          {[["player", "Player"], ["team", "Team"]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k}
              className={`sl-toggle-btn ${scope === k ? "is-active" : ""}`} onClick={() => setScope(k)} data-testid={`sl-scope-${k}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="sl-grid">
        {groups.map(([title, stats]) => (
          <div className="sl-col" key={title}>
            <h2 className="sl-col-title">{title}</h2>
            {stats.map((s) => <LeaderCard key={s} stat={s} rows={(data[scope] || {})[s] || []} scope={scope} onComplete={(st) => navigate(`/stats/all?sort=${st}`)} />)}
          </div>
        ))}
      </div>
      <p className="sl-note">Season averages · updated after each sync</p>
    </div>
  );
}
